import { useState, useEffect, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  AccessibilityHelp,
  Autoformat,
  Autosave,
  BalloonToolbar,
  // BlockQuote,
  Bold,
  CloudServices,
  Essentials,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  HtmlComment,
  HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  MediaEmbed,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  ShowBlocks,
  SourceEditing,
  Strikethrough,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  Undo,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";

import { APIURL } from "../../services/axiosServices/ApiEndPoints";

// const API_URL = "http://localhost:5099/";

function CKClassicEditor({ handleChange, ...props }) {
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [editorData, setEditorData] = useState(props.data || "<div></div>");

  useEffect(() => {
    setEditorData(props.data || "<div></div>");
  }, [props.data]);

  useEffect(() => {
    setIsLayoutReady(true);
    return () => setIsLayoutReady(false);
  }, []);

  /** -------- Custom Upload Adapter -------- **/
  class MyCustomUploadAdapter {
    constructor(loader) {
      this.loader = loader;
      this.controller = new AbortController();
    }

    async upload() {
      console.log("UPLOAD TRIGGERED ✅");
      try {
        const file = await this.loader.file;

        if (!file) throw new Error("No file found"); // ---- Upload to API ----

        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          `${APIURL}API/Common/CKEditorImageUpload`,
          {
            method: "POST",
            body: formData,
            signal: this.controller.signal,
          }
        );

        if (!response.ok)
          throw new Error(`Upload failed: ${response.statusText}`);

        const result = await response.json();

        if (!result?.location) {
          throw new Error("No image URL returned from server");
        }

        return { default: result.location };
      } catch (error) {
        console.error("Upload error:", error);
        return Promise.reject(error);
      }
    }

    abort() {
      this.controller.abort();
    }
  }

  function uploadPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return new MyCustomUploadAdapter(loader);
    };
  }

  /** -------- Other Plugins -------- **/

  /** Default text color without setData loop */
  function defaultTextColorPlugin(editor) {
    editor.model.document.on("change:data", () => {
      editor.model.change((writer) => {
        for (const element of editor.model.document.getRoot().getChildren()) {
          if (element.is("element") && !element.getAttribute("color")) {
            writer.setAttribute("color", "#000", element);
          }
        }
      });
    });
  }

  function AllowSpanPlugin(editor) {
    editor.model.schema.register("span", {
      allowWhere: "$text",
      isInline: true,
      isObject: false,
      allowAttributes: ["class", "style", "id"],
    });

    editor.conversion.elementToElement({
      model: "span",
      view: {
        name: "span",
        classes: true,
        styles: true,
        attributes: ["id"],
      },
    });
  }

  function ImageWidthToImgPlugin(editor) {
    // Patch how data is exported (editor.getData)
    const toData = editor.data.processor.toData;

    editor.data.processor.toData = (viewFragment) => {
      let html = toData.call(editor.data.processor, viewFragment);

      // Find <figure ...><img .../></figure>
      html = html.replace(
        /<figure([^>]*)>([\s\S]*?<img[^>]+>)[\s\S]*?<\/figure>/gi,
        (match, figureAttrs, imgTag) => {
          let newImg = imgTag;

          // If figure has width style, move it to <img>
          const widthMatch = figureAttrs.match(
            /style="[^"]*width:\s*([^;"]+)/i
          );
          if (widthMatch) {
            const width = widthMatch[1].trim();

            if (!/style=/.test(newImg)) {
              newImg = newImg.replace("<img", `<img style="width:${width}"`);
            } else {
              newImg = newImg.replace(/style="/, `style="width:${width}; `);
            }
          }

          // Keep figure (for alignment), but width only on img
          return `<figure${figureAttrs}>${newImg}</figure>`;
        }
      );

      return html;
    };
  }

  function autoHeightImagePlugin(editor) {
    editor.model.document.on("change:data", () => {
      editor.model.change((writer) => {
        for (const element of editor.model.document.getRoot().getChildren()) {
          if (
            element.name === "imageBlock" &&
            !element.getAttribute("height")
          ) {
            writer.setAttribute("height", "auto", element);
          }
        }
      });
    });
  }

  /** -------- CKEditor Config -------- **/
  const editorConfig = {
    extraPlugins: [
      uploadPlugin,
      autoHeightImagePlugin,
      AllowSpanPlugin,
      defaultTextColorPlugin,
      ImageWidthToImgPlugin,
    ],
    toolbar: {
      items: [
        "undo",
        "redo",
        "|",
        "sourceEditing",
        "showBlocks",
        "|",
        "heading",
        "|",
        "bold",
        "italic",
        "underline",
        "|",
        "link",
        "insertImage",
        "insertTable",
        // "blockQuote",
        "|",
        "bulletedList",
        "numberedList",
        "todoList",
        "outdent",
        "indent",
        "fontColor",
        "fontBackgroundColor",
        "strikethrough",
        "superscript",
        "fontFamily",
        "fontSize",
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AccessibilityHelp,
      Autoformat,
      Autosave,
      BalloonToolbar,
      // BlockQuote,
      Bold,
      CloudServices,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      Essentials,
      FullPage,
      GeneralHtmlSupport,
      Heading,
      HtmlComment,
      HtmlEmbed,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsert,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      Paragraph,
      PasteFromOffice,
      SelectAll,
      ShowBlocks,
      SourceEditing,
      Strikethrough,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      TodoList,
      Underline,
      Undo,
      FontSize,
    ],
    // ✅ Add font settings here
    fontFamily: {
      options: [
        "default",
        "Arial, Helvetica, sans-serif",
        "Times New Roman, Times, serif",
        "Verdana, Geneva, sans-serif",
        "Tahoma, Geneva, sans-serif",
      ],
      supportAllValues: true,
      default: "Arial, Helvetica, sans-serif", // 👈 Default font
    },
    fontSize: {
      options: [10, 12, 14, 16, 18, 20, 24, 28, 32, 36],
      supportAllValues: true,
      default: 14, // 👈 Default font size (px)
    },

    balloonToolbar: [
      "bold",
      "italic",
      "|",
      "link",
      "|",
      "bulletedList",
      "numberedList",
    ],
    heading: {
      options: [
        {
          model: "paragraph",
          title: "Paragraph",
          class: "ck-heading_paragraph",
        },
        {
          model: "heading1",
          view: "h1",
          title: "Heading 1",
          class: "ck-heading_heading1",
        },

        {
          model: "heading2",
          view: "h2",
          title: "Heading 2",
          class: "ck-heading_heading2",
        },

        {
          model: "heading3",
          view: "h3",
          title: "Heading 3",
          class: "ck-heading_heading3",
        },

        {
          model: "heading4",

          view: "h4",

          title: "Heading 4",

          class: "ck-heading_heading4",
        },
      ],
      defaultHeading: "Choose heading",
    },

    htmlSupport: {
      allow: [
        {
          name: /^.*$/,

          styles: true,

          attributes: true,

          classes: true,
        },
      ],
    },

    image: {
      resizeUnit: "px",
      resizeOptions: [
        { name: "resizeImage:original", label: "Original", value: null },
        { name: "resizeImage:50", label: "50%", value: "50" },
        { name: "resizeImage:75", label: "75%", value: "75" },
      ],
      toolbar: [
        "toggleImageCaption",
        "imageTextAlternative",
        "|",
        "imageStyle:inline",
        "imageStyle:wrapText",
        "imageStyle:breakText",
        "|",
        "resizeImage",
      ],
    },

    placeholder: "Type or paste your content here!",
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: "https://",
      decorators: {
        toggleDownloadable: {
          mode: "manual",
          label: "Downloadable",
          attributes: {
            download: "file",
          },
        },
      },
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },
    menuBar: {
      isVisible: true,
    },

    list: { properties: { styles: true, startIndex: true, reversed: true } },
    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableProperties",
        "tableCellProperties",
      ],
    },
  };

  return (
    <div className="ckeditor-style-for-emailtemplate" ref={editorContainerRef}>
      <div ref={editorRef}>
        {isLayoutReady && (
          <CKEditor
            editor={ClassicEditor}
            data={editorData}
            config={editorConfig}
            
            onChange={(event, editor) => {
              const data = editor.getData();
              props.onChange(data, editor);
            }}
            
          />
        )}
      </div>
    </div>
  );
}

export default CKClassicEditor;
